/**
 * Procurement Path A — server-side aggregate + contract table (replaces client 30–60k filtering).
 */

import { expandCategoriesForApi } from '@/utils/edtechCategoryNames';

const API_BASE_URL =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'https://api.neogtm.com'
    : 'https://api.neogtm.com';

function appendList(params, key, values) {
  const list = Array.isArray(values) ? values.filter(Boolean) : [];
  if (list.length) params.set(key, list.join(','));
}

/** Build query string aligned with GET /api/procurement/aggregate and /contract-table. */
export function buildProcurementFilterParams(filters = {}) {
  const params = new URLSearchParams();
  appendList(params, 'categories', expandCategoriesForApi(filters.categories));
  appendList(params, 'countries', filters.countries);
  appendList(params, 'institutionTypes', filters.institutionTypes);
  // [2026-10-17] `statuses` was the one Contract Intelligence control that never reached the
  // server. The BE has accepted it all along (splitParam(req.query.statuses) -> p_statuses in the
  // shared filter builder behind /aggregate, /contract-table and /query); only this line was
  // missing, so Status=Active was silently a no-op on the server-backed path.
  appendList(params, 'statuses', filters.statuses);
  appendList(params, 'suppliers', filters.suppliers);
  appendList(params, 'productNames', filters.productNames);
  // [2026-10-13] Contract table only — the hook never puts this on the aggregate's filters.
  appendList(params, 'frameworks', filters.frameworks);
  appendList(params, 'resellers', filters.resellers);
  appendList(params, 'decisionWindows', filters.decisionWindows);
  appendList(params, 'search', filters.search ? [String(filters.search).trim()] : []);

  if (filters.sentiments?.length) {
    appendList(params, 'sentiments', filters.sentiments);
  }

  if (filters.sort) params.set('sort', filters.sort);
  if (filters.dir) params.set('dir', filters.dir === 'ascending' ? 'asc' : 'desc');
  if (filters.page != null) params.set('page', String(filters.page));
  if (filters.pageSize != null) params.set('pageSize', String(filters.pageSize));
  if (filters.exitFilter && filters.exitFilter !== 'all') {
    params.set('exitFilter', filters.exitFilter);
  }

  return params;
}

function headers(tenantId) {
  return {
    'Content-Type': 'application/json',
    'x-tenant-id': tenantId,
  };
}

async function parseJson(res) {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { error: text };
  }
}

export async function fetchProcurementAggregate(tenantId, filters) {
  const qs = buildProcurementFilterParams(filters).toString();
  const url = `${API_BASE_URL}/api/procurement/aggregate${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { credentials: 'include', headers: headers(tenantId) });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(data?.error || data?.message || `Aggregate failed (${res.status})`);
  }
  return data;
}

export async function fetchAllProcurementContractRows(tenantId, filters) {
  // [2026-10-10] 200 -> 2000. /contract-table re-runs the whole aggregation per call,
  // so cost is fixed per request, not per row: 25 pages at 200 took ~34s of sequential
  // round trips (observed in Cloud Run logs as pages 1..25 at ~1.8s each), while 3
  // pages at 2000 take ~6s. The server clamp was raised to match.
  const pageSize = 2000;
  const rows = [];
  const seen = new Set();
  let totalAccounts = 0;
  let totalDeals = 0;

  for (let page = 1; page <= 100; page += 1) {
    const tbl = await fetchProcurementContractTable(tenantId, {
      ...filters,
      page,
      pageSize,
    });
    const batch = Array.isArray(tbl?.rows) ? tbl.rows : [];
    totalAccounts = Number(tbl?.total_accounts) || totalAccounts;
    totalDeals = Number(tbl?.total_deals) || totalDeals;

    let added = 0;
    for (const row of batch) {
      const id = String(row?.deal_id || row?.id || '');
      if (id) {
        if (seen.has(id)) continue;
        seen.add(id);
      }
      rows.push(row);
      added += 1;
    }

    if (added === 0) break;
    if (totalAccounts > 0 && rows.length >= totalAccounts) break;
  }

  return { rows, total_accounts: totalAccounts || rows.length, total_deals: totalDeals };
}

export async function fetchProcurementContractTable(tenantId, filters) {
  const qs = buildProcurementFilterParams({
    ...filters,
    page: filters.page ?? 1,
    pageSize: filters.pageSize ?? 50,
  }).toString();
  const url = `${API_BASE_URL}/api/procurement/contract-table?${qs}`;
  const res = await fetch(url, { credentials: 'include', headers: headers(tenantId) });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(data?.error || data?.message || `Contract table failed (${res.status})`);
  }
  return data;
}

export async function fetchProcurementDisplacementCells(tenantId, filters) {
  const qs = buildProcurementFilterParams(filters).toString();
  const url = `${API_BASE_URL}/api/procurement/displacement-cells${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { credentials: 'include', headers: headers(tenantId) });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(data?.error || data?.message || `Displacement cells failed (${res.status})`);
  }
  return data;
}

export async function fetchProcurementBenchmarkPoints(tenantId, filters) {
  const qs = buildProcurementFilterParams(filters).toString();
  const url = `${API_BASE_URL}/api/procurement/benchmark-points${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { credentials: 'include', headers: headers(tenantId) });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(data?.error || data?.message || `Benchmark points failed (${res.status})`);
  }
  return data;
}

/** Map UI sort keys to API sort param. */
export function mapSortKeyToApi(sortKey) {
  switch (sortKey) {
    case 'institution':
      return 'institution';
    case 'country':
      return 'country';
    case 'category':
      return 'category';
    case 'supplier':
      return 'supplier';
    case 'product_name':
      return 'product_name';
    case 'expiry':
      return 'expiry';
    case 'sentiment':
      return 'sentiment';
    case 'value':
      return 'value';
    case 'decision_window':
    case 'soonest':
    default:
      return 'soonest';
  }
}

/**
 * Displacement Radar v2 — per-category intelligence.
 *
 * Uses the same transport as every other call in this module on purpose:
 * `credentials: 'include'` is load-bearing. The API's tenant gate runs on
 * /api before routing and resolves the tenant from the session; without the
 * cookie it never sets requestedTenantId and answers 403 "Invalid or missing
 * tenant_id" regardless of the x-tenant-id header. tenant_id also goes on the
 * query string, which the gate accepts directly, so the call still resolves if
 * the session is missing or expired.
 *
 * No country/vendor filters by design: churn needs each account's full award
 * history, and a filtered history biases it. Only the categories are scoped.
 */
/**
 * Procurement › Route to Buy — how one market buys (framework / reseller / direct), the vehicles
 * that carry it and the resellers in it. Computed live server-side (procurement_route_to_buy);
 * `categories` empty = all categories.
 */
export async function fetchRouteToBuy(tenantId, country, categories = []) {
  const params = new URLSearchParams();
  params.set('country', country);
  const list = (categories || []).filter(Boolean);
  if (list.length) params.set('categories', list.join('|'));
  if (tenantId) params.set('tenant_id', tenantId);
  const url = `${API_BASE_URL}/api/procurement/route-to-buy?${params.toString()}`;
  const res = await fetch(url, { credentials: 'include', headers: headers(tenantId) });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data?.error || data?.message || `Route to Buy failed (${res.status})`);
  return data;
}

/**
 * Procurement › Route to Buy verdicts (2026-11-02) — the conclusions per market: local entity, frameworks to join,
 * procurement friendliness of public institutions, public share and LMS/SIS integrations, plus market signals.
 * Precomputed server-side (rtm_verdict_cache). `markets` lists only markets with enough evidence.
 */
export async function fetchRouteVerdicts(tenantId, country) {
  const params = new URLSearchParams();
  if (country) params.set('country', country);
  if (tenantId) params.set('tenant_id', tenantId);
  const url = `${API_BASE_URL}/api/procurement/route-verdicts?${params.toString()}`;
  const res = await fetch(url, { credentials: 'include', headers: headers(tenantId) });
  const data = await parseJson(res);
  if (!res.ok) {
    const err = new Error(data?.error || data?.message || `Route verdicts failed (${res.status})`);
    err.markets = data?.markets;
    err.status = res.status;
    throw err;
  }
  return data;
}

export async function fetchCategoryIntel(tenantId, categories = [], country = null) {
  const list = (categories || []).filter(Boolean);
  if (!list.length) return { categories: [], computed_at: null };
  const params = new URLSearchParams();
  params.set('categories', list.join('|'));
  if (tenantId) params.set('tenant_id', tenantId);
  // Only the markets the server allows; anything else is ignored there and
  // would silently come back global.
  if (country) params.set('country', country);
  const url = `${API_BASE_URL}/api/procurement/category-intel?${params.toString()}`;
  const res = await fetch(url, { credentials: 'include', headers: headers(tenantId) });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(data?.error || data?.message || `Category intel failed (${res.status})`);
  }
  return data;
}

/** Accounts behind one cell of the sentiment x decision-window grid. */
/** The supplier changes behind one Displacement Radar net winner / loser row (hover pop-up). */
export async function fetchCategoryIntelSupplierMoves(tenantId, { category, supplier, country }) {
  const params = new URLSearchParams({ category, supplier });
  if (country) params.set('country', country);
  if (tenantId) params.set('tenant_id', tenantId);
  const url = `${API_BASE_URL}/api/procurement/category-intel/supplier-moves?${params.toString()}`;
  const res = await fetch(url, { credentials: 'include', headers: headers(tenantId) });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data?.error || data?.message || `Supplier moves failed (${res.status})`);
  return data;
}

export async function fetchCategoryIntelAccounts(tenantId, { category, sentiment, window }) {
  const params = new URLSearchParams({ category, sentiment, window: String(window) });
  if (tenantId) params.set('tenant_id', tenantId);
  const url = `${API_BASE_URL}/api/procurement/category-intel/accounts?${params.toString()}`;
  const res = await fetch(url, { credentials: 'include', headers: headers(tenantId) });
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(data?.error || data?.message || `Category accounts failed (${res.status})`);
  }
  return data;
}

/**
 * [2026-11-03] Replacement cycle — the HARDWARE view of Contracts Intelligence and the
 * Displacement Radar, read from the API's precomputed store (api/src/services/replacementCycle.ts,
 * migration 2026-11-03_replacement_cycle_tables). Filtered, sorted and paged server-side: IT
 * Hardware alone is 77k deals. The server 400s any non-hardware category.
 */
async function getReplacementCycle(tenantId, path, params) {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (Array.isArray(v)) { if (v.length) qs.set(k, v.join('|')); } else if (v != null && v !== '') qs.set(k, String(v));
  });
  if (tenantId) qs.set('tenant_id', tenantId);
  const res = await fetch(`${API_BASE_URL}/api/procurement/replacement-cycle/${path}?${qs.toString()}`, {
    credentials: 'include', headers: headers(tenantId),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data?.error || data?.message || `Replacement cycle failed (${res.status})`);
  return data;
}

/** One page of installs plus the KPI totals and band counts for the current filters. */
export function fetchReplacementCyclePage(tenantId, {
  category, countries = [], vendors = [], types = [], sentiments = [], bands = [], search = '',
  sort = 'age', dir = 'desc', page = 1, pageSize = 50,
} = {}) {
  return getReplacementCycle(tenantId, 'page', { category, countries, vendors, types, sentiments, bands, search, sort, dir, page, pageSize });
}

/** The radar summary for one category, optionally one country. */
export function fetchReplacementCycleRadar(tenantId, category, country = null) {
  return getReplacementCycle(tenantId, 'radar', { category, country });
}
